import React, { useState, useMemo, useCallback } from 'react'
import { useAppData } from '../../context/AppContext'
import { useAuth } from '../../context/AuthContext'
import { useSubscription } from '../../context/SubscriptionContext'
import { currencyService } from '../../services/currencyService'
import { dateCalculationService } from '../../services/dateCalculationService'
import { allocationDistributionService } from '../../services/allocationDistributionService'
import { ArrowLeft, ArrowRight, Download, Shield, Calendar } from '../icons/Icons'
import GanttChart from './GanttChart'
import s from './shared.module.css'
import rs from './Reports.module.css'

type ViewType = 'day' | 'week' | 'month' | 'quarter' | 'year'
type ReportTab = 'profitability' | 'nonbillable' | 'utilization' | 'revenue' | 'forecastvactual' | 'projectanalysis' | 'gantt'

const parseDateOnlyUTC = (dateStr: string) => {
  const [y, m, d] = dateStr.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d))
}

export default function Reports() {
  debugger;
  const { customers, projects, resources, allocations, allAllocations, timesheets, timesheetEntriesV2, resourceRoleTypes, holidays, timeOffRequests, projectProfitabilityReports, nonBillableReports, partners, refreshData, getResourceTimeOffDates } = useAppData()
  const { organization } = useAuth()
  const { canAccess } = useSubscription()
  const [selectedReport, setSelectedReport] = useState<ReportTab>('projectanalysis')
  const [profFilter, setProfFilter] = useState({ name: '', customer: '', status: '' })
  const [nbFilter, setNbFilter] = useState({ resource: '', project: '' })
  const [utilView, setUtilView] = useState<ViewType>('month')
  const [utilDate, setUtilDate] = useState(new Date())
  const [revFilter, setRevFilter] = useState({ customer: '', status: '' })
  const [fvaFilter, setFvaFilter] = useState({ resource: '', project: '' })
  const [paFilter, setPaFilter] = useState({ project: '', customer: '' })
  const [selectedResourceId, setSelectedResourceId] = useState<string | null>(null)
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null)

  const toUTCDateOnly = (d: Date) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()))
  const normalizeEnd = (d: Date) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 23, 59, 59, 999))

  const getUtilRange = () => {
    switch (utilView) {
      case 'day': return { start: utilDate, end: utilDate }
      case 'week': return { start: dateCalculationService.getStartOfWeek(utilDate), end: dateCalculationService.getEndOfWeek(utilDate) }
      case 'month': return { start: dateCalculationService.getStartOfMonth(utilDate), end: dateCalculationService.getEndOfMonth(utilDate) }
      case 'quarter': return { start: dateCalculationService.getStartOfQuarter(utilDate), end: dateCalculationService.getEndOfQuarter(utilDate) }
      case 'year': return { start: dateCalculationService.getStartOfYear(utilDate), end: dateCalculationService.getEndOfYear(utilDate) }
    }
  }
  const { start: utilStart, end: utilEnd } = getUtilRange()
  const utilStartUTC = toUTCDateOnly(utilStart)
  const utilEndUTC = normalizeEnd(utilEnd)

  const navigateUtil = (dir: -1 | 1) => {
    const d = new Date(utilDate)
    switch (utilView) {
      case 'day': d.setUTCDate(d.getUTCDate() + dir); break
      case 'week': d.setUTCDate(d.getUTCDate() + 7 * dir); break
      case 'month': d.setUTCMonth(d.getUTCMonth() + dir); break
      case 'quarter': d.setUTCMonth(d.getUTCMonth() + 3 * dir); break
      case 'year': d.setUTCFullYear(d.getUTCFullYear() + dir); break
    }
    setUtilDate(d)
  }

  const generateResourceTimeline = useCallback((resourceId: string) => {
    const resource = resources.find(r => r.id === resourceId)
    if (!resource) return []

    const resourceAllocations = allocations.filter(a => a.resource_id === resourceId)

    return resourceAllocations.map(allocation => {
      const project = projects.find(p => p.id === allocation.project_id)
      const customer = customers.find(c => c.id === project?.customer_id)

      return {
        allocation,
        project: project?.title || 'Unknown Project',
        customer: customer?.title || 'Unknown Customer',
        startDate: parseDateOnlyUTC(allocation.allocation_start),
        endDate: parseDateOnlyUTC(allocation.allocation_end),
        duration: Math.ceil((parseDateOnlyUTC(allocation.allocation_end).getTime() - parseDateOnlyUTC(allocation.allocation_start).getTime()) / (1000 * 60 * 60 * 24)),
        status: project?.status || 'Unknown'
      }
    }).sort((a, b) => a.startDate.getTime() - b.startDate.getTime())
  }, [resources, allocations, projects, customers])

  const generateProjectTimeline = useCallback((projectId: string) => {
    const project = projects.find(p => p.id === projectId)
    if (!project) return []

    const projectAllocations = allocations.filter(a => a.project_id === projectId)

    return projectAllocations.map(allocation => {
      const resource = resources.find(r => r.id === allocation.resource_id)
      const roleType = resourceRoleTypes.find(rt => rt.id === allocation.role_type_id)
      const workingDays = dateCalculationService.getWorkingDays(
        allocation.allocation_start,
        allocation.allocation_end,
        holidays
      )
      const totalHours = allocationDistributionService.calculateTotalHours(
        allocation.allocation_start,
        allocation.allocation_end,
        allocation.daily_hours,
        allocation.allocation_interval,
        holidays
      )

      return {
        allocation,
        resource: resource?.title || 'Unknown Resource',
        role: roleType?.title || 'Unknown Role',
        startDate: parseDateOnlyUTC(allocation.allocation_start),
        endDate: parseDateOnlyUTC(allocation.allocation_end),
        duration: workingDays,
        billingRate: roleType?.billing_rate || 0
      }
    }).sort((a, b) => a.startDate.getTime() - b.startDate.getTime())
  }, [projects, allocations, resources, resourceRoleTypes, holidays])

  const filteredProfitability = useMemo(() => projectProfitabilityReports.filter(r => {
    if (profFilter.name && !r.projectTitle.toLowerCase().includes(profFilter.name.toLowerCase())) return false
    if (profFilter.customer && !r.customerName.toLowerCase().includes(profFilter.customer.toLowerCase())) return false
    if (profFilter.status && r.status !== profFilter.status) return false
    return true
  }), [projectProfitabilityReports, profFilter])

  const filteredNonBillable = useMemo(() => nonBillableReports.filter(r => {
    if (nbFilter.resource && !r.resourceName.toLowerCase().includes(nbFilter.resource.toLowerCase())) return false
    if (nbFilter.project && !r.projectTitle.toLowerCase().includes(nbFilter.project.toLowerCase())) return false
    return true
  }), [nonBillableReports, nbFilter])

  const utilizationData = useMemo(() => {
    const rangeStartStr = utilStartUTC.toISOString().split('T')[0]
    const rangeEndStr = utilEndUTC.toISOString().split('T')[0]
    return resources.filter(r => r.active).map(resource => {
      const timeOffDates = getResourceTimeOffDates(resource.id, rangeStartStr, rangeEndStr)
      const availableDays = dateCalculationService.getAvailableWorkingDaysInRange(utilStartUTC, utilEndUTC, holidays, timeOffDates)
      const workingDays = dateCalculationService.getWorkingDaysInRange(utilStartUTC, utilEndUTC, holidays)
      const timeOffDays = workingDays - availableDays
      const capacity = resource.availability * availableDays
      const allocated = allocations.reduce((total, alloc) => {
        if (alloc.resource_id !== resource.id) return total
        const allocStart = parseDateOnlyUTC(alloc.allocation_start)
        const allocEnd = parseDateOnlyUTC(alloc.allocation_end)
        const overlapStart = allocStart > utilStartUTC ? allocStart : utilStartUTC
        const overlapEnd = allocEnd < utilEndUTC ? allocEnd : utilEndUTC
        if (overlapStart > overlapEnd) return total
        const overlapHours = allocationDistributionService.getHoursForDateRange(
          alloc.allocation_start,
          alloc.allocation_end,
          alloc.daily_hours,
          alloc.allocation_interval,
          overlapStart.toISOString().split('T')[0],
          overlapEnd.toISOString().split('T')[0],
          holidays,
          timeOffDates
        )
        return total + overlapHours
      }, 0)
      return { resource, capacity, allocated, utilization: capacity > 0 ? (allocated / capacity) * 100 : 0, workingDays: availableDays, timeOffDays }
    }).sort((a, b) => b.utilization - a.utilization)
  }, [resources, allocations, holidays, utilStartUTC, utilEndUTC, getResourceTimeOffDates])

  const revenueForecastData = useMemo(() => {
    const today = new Date()
    const months: { year: number; month: number; label: string }[] = []
    for (let i = 0; i < 6; i++) {
      const d = new Date(Date.UTC(today.getFullYear(), today.getMonth() + i, 1))
      months.push({ year: d.getUTCFullYear(), month: d.getUTCMonth(), label: d.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' }) })
    }

    const projectRows = projects.map(proj => {
      const customer = customers.find(c => c.id === proj.customer_id)
      const projAllocs = allAllocations.filter(a => a.project_id === proj.id && a.billable)
      const monthlyRevenue = months.map(m => {
        let rev = 0
        projAllocs.forEach(alloc => {
          const billingRate = alloc.billing_rate_at_allocation || resourceRoleTypes.find(rt => rt.id === alloc.role_type_id)?.billing_rate || 0
          const currency = alloc.currency_at_allocation || resourceRoleTypes.find(rt => rt.id === alloc.role_type_id)?.currency || 'USD'
          const monthStart = new Date(Date.UTC(m.year, m.month, 1))
          const monthEnd = new Date(Date.UTC(m.year, m.month + 1, 0))
          const rangeStart = monthStart.toISOString().split('T')[0]
          const rangeEnd = monthEnd.toISOString().split('T')[0]
          const timeOffDates = getResourceTimeOffDates(alloc.resource_id, rangeStart, rangeEnd)
          const monthHours = allocationDistributionService.getHoursForDateRange(
            alloc.allocation_start,
            alloc.allocation_end,
            alloc.daily_hours,
            alloc.allocation_interval,
            rangeStart,
            rangeEnd,
            holidays,
            timeOffDates
          )
          rev += currencyService.convert(billingRate * monthHours, currency, 'USD').convertedAmount
        })
        return rev
      })
      const total = monthlyRevenue.reduce((s, v) => s + v, 0)
      return { proj, customer, monthlyRevenue, total }
    }).filter(r => r.total > 0)

    const monthTotals = months.map((_, mi) => projectRows.reduce((s, r) => s + r.monthlyRevenue[mi], 0))
    const grandTotal = monthTotals.reduce((s, v) => s + v, 0)

    return { months, projectRows, monthTotals, grandTotal }
  }, [projects, allAllocations, customers, resourceRoleTypes, holidays, getResourceTimeOffDates])

  const filteredRevenueForecast = useMemo(() => {
    return revenueForecastData.projectRows.filter(r => {
      if (revFilter.customer && !r.customer?.title.toLowerCase().includes(revFilter.customer.toLowerCase())) return false
      if (revFilter.status && r.proj.status !== revFilter.status) return false
      return true
    })
  }, [revenueForecastData, revFilter])

  const forecastVsActualData = useMemo(() => {
    return resources.map(resource => {
      const resAllocs = allAllocations.filter(a => a.resource_id === resource.id && a.allocation_type === 'Forecast')
      const resEntries = timesheetEntriesV2.filter(t => t.resource_id === resource.id)

      let forecastHours = 0
      resAllocs.forEach(alloc => {
        const timeOffDates = getResourceTimeOffDates(alloc.resource_id, alloc.allocation_start, alloc.allocation_end)
        forecastHours += allocationDistributionService.calculateTotalHours(
          alloc.allocation_start,
          alloc.allocation_end,
          alloc.daily_hours,
          alloc.allocation_interval,
          holidays,
          timeOffDates
        )
      })

      const actualBillable = resEntries.filter(t => t.is_billable).reduce((s, t) => s + t.hours, 0)
      const actualNonBillable = resEntries.filter(t => !t.is_billable).reduce((s, t) => s + t.hours, 0)
      const actualTotal = actualBillable + actualNonBillable

      const variance = actualTotal - forecastHours
      const variancePct = forecastHours > 0 ? (variance / forecastHours) * 100 : 0

      return { resource, forecastHours, actualBillable, actualNonBillable, actualTotal, variance, variancePct }
    }).filter(r => r.forecastHours > 0 || r.actualTotal > 0)
  }, [resources, allAllocations, timesheetEntriesV2, holidays, getResourceTimeOffDates])

  const filteredForecastVsActual = useMemo(() => {
    return forecastVsActualData.filter(r => {
      if (fvaFilter.resource && !r.resource.title.toLowerCase().includes(fvaFilter.resource.toLowerCase())) return false
      return true
    })
  }, [forecastVsActualData, fvaFilter])

  const projectAnalysisData = useMemo(() => {
    return projects.map(proj => {
      const customer = customers.find(c => c.id === proj.customer_id)
      const projAllocs = allAllocations.filter(a => a.project_id === proj.id)
      const uniqueResources = Array.from(new Set(projAllocs.map(a => a.resource_id)))
      let totalForecastHours = 0, totalBillableHours = 0, totalNonBillableHours = 0
      let totalRevenue = 0, totalCost = 0

      projAllocs.forEach(alloc => {
        const timeOffDates = getResourceTimeOffDates(alloc.resource_id, alloc.allocation_start, alloc.allocation_end)
        const hours = allocationDistributionService.calculateTotalHours(
          alloc.allocation_start,
          alloc.allocation_end,
          alloc.daily_hours,
          alloc.allocation_interval,
          holidays,
          timeOffDates
        )
        const billingRate = alloc.billing_rate_at_allocation || resourceRoleTypes.find(rt => rt.id === alloc.role_type_id)?.billing_rate || 0
        const costRate = alloc.cost_rate_at_allocation || resourceRoleTypes.find(rt => rt.id === alloc.role_type_id)?.cost_rate || 0
        const currency = alloc.currency_at_allocation || resourceRoleTypes.find(rt => rt.id === alloc.role_type_id)?.currency || 'USD'
        totalForecastHours += hours
        if (alloc.billable) {
          totalBillableHours += hours
          totalRevenue += currencyService.convert(billingRate * hours, currency, 'USD').convertedAmount
        } else {
          totalNonBillableHours += hours
        }
        totalCost += currencyService.convert(costRate * hours, currency, 'USD').convertedAmount
      })

      const tsHours = timesheets.filter(t => t.project_id === proj.id).reduce((s, t) => s + t.billable_hours + t.non_billable_hours, 0)

      return {
        proj, customer, uniqueResources: uniqueResources.length,
        totalForecastHours, totalBillableHours, totalNonBillableHours, totalRevenue, totalCost,
        profit: totalRevenue - totalCost, tsHours,
      }
    }).filter(r => r.totalForecastHours > 0 || r.tsHours > 0)
  }, [projects, allAllocations, customers, resourceRoleTypes, holidays, timesheets, getResourceTimeOffDates])

  const filteredProjectAnalysis = useMemo(() => {
    return projectAnalysisData.filter(r => {
      if (paFilter.project && !r.proj.title.toLowerCase().includes(paFilter.project.toLowerCase())) return false
      if (paFilter.customer && !r.customer?.title.toLowerCase().includes(paFilter.customer.toLowerCase())) return false
      return true
    })
  }, [projectAnalysisData, paFilter])

  const exportCSV = (data: object[], filename: string) => {
    if (!data.length) return
    const headers = Object.keys(data[0])
    const rows = data.map(r => headers.map(h => (r as any)[h]).join(','))
    const csv = [headers.join(','), ...rows].join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = filename; a.click()
    URL.revokeObjectURL(url)
  }

  const reportTabs: { id: ReportTab; label: string; requires?: 'has_revenue_forecast' | 'has_gantt' }[] = [
    { id: 'projectanalysis', label: 'Project Analysis' },
    { id: 'revenue', label: 'Revenue Forecast', requires: 'has_revenue_forecast' },
    { id: 'utilization', label: 'Utilization' },
    { id: 'profitability', label: 'Project Profitability' },
    { id: 'forecastvactual', label: 'Forecast vs Actual', requires: 'has_revenue_forecast' },
    { id: 'nonbillable', label: 'Non-Billable' },
    { id: 'gantt', label: 'Gantt Chart', requires: 'has_gantt' },
  ]

  const TimelineView: React.FC<{
    items: any[]
    type: 'resource' | 'project'
    onBack: () => void
    title: string
  }> = ({ items, type, onBack, title }) => {
    const today = new Date()

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24 }}>
          <button onClick={onBack} className={s.secondaryButton} style={{ marginRight: 16 }}>
            ← Back to {type === 'resource' ? 'Resources' : 'Projects'}
          </button>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#323130' }}>
            {title} - Timeline View
          </h3>
        </div>

        <div className={s.card}>
          <div className={s.cardContent}>
            {items.length > 0 ? (
              <div style={{ overflowX: 'auto' }}>
                <div style={{ minWidth: 800 }}>
                  {items.map((item, index) => {
                    const isActive = today >= item.startDate && today <= item.endDate
                    const isPast = today > item.endDate

                    return (
                      <div key={index} style={{
                        marginBottom: 16,
                        padding: 16,
                        border: '1px solid #edebe9',
                        borderRadius: 4,
                        backgroundColor: isActive ? '#f0f9ff' : isPast ? '#f8f9fa' : '#ffffff'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                          <div>
                            <div style={{ fontSize: 16, fontWeight: 600, color: '#323130', marginBottom: 4 }}>
                              {type === 'resource' ? item.project : item.resource}
                            </div>
                            <div style={{ fontSize: 14, color: '#605e5c' }}>
                              {type === 'resource' ? item.customer : item.role}
                            </div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: 14, fontWeight: 600, color: '#323130' }}>
                              {item.allocation.daily_hours}h
                              {item.allocation.allocation_interval === 'Daily' ? '/day' : item.allocation.allocation_interval === 'Weekly' ? '/week' : '/month'}
                            </div>
                            {type === 'project' && (
                              <div style={{ fontSize: 12, color: '#605e5c' }}>
                                {currencyService.formatCurrency(item.billingRate, 'USD', 0)}
                              </div>
                            )}
                          </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8 }}>
                          <div style={{ fontSize: 14, color: '#605e5c' }}>
                            {item.startDate.toLocaleDateString()} - {item.endDate.toLocaleDateString()}
                          </div>
                          <div style={{ fontSize: 14, color: '#605e5c' }}>
                            {item.duration} days
                          </div>
                          <span className={`${s.badge} ${isActive ? s.success : isPast ? s.neutral : s.info}`}>
                            Timeline: {isActive ? 'Active' : isPast ? 'Completed' : 'Upcoming'}
                          </span>
                          {type === 'resource' && (
                            <span className={`${s.badge} ${
                              item.status === 'Active' ? s.success :
                              item.status === 'On Hold' ? s.warning :
                              s.error
                            }`}>
                              Project: {item.status}
                            </span>
                          )}
                        </div>

                        <div style={{ position: 'relative', height: 8, backgroundColor: '#edebe9', borderRadius: 4, marginTop: 12 }}>
                          <div style={{
                            position: 'absolute',
                            left: '0%',
                            width: '100%',
                            height: '100%',
                            backgroundColor: isActive ? '#107c10' : isPast ? '#8a8886' : '#0078d4',
                            borderRadius: 4
                          }} />
                          {isActive && (
                            <div style={{
                              position: 'absolute',
                              left: `${((today.getTime() - item.startDate.getTime()) / (item.endDate.getTime() - item.startDate.getTime())) * 100}%`,
                              top: -2,
                              width: 2,
                              height: 12,
                              backgroundColor: '#d13438'
                            }} />
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: 40, color: '#605e5c' }}>
                <Calendar size={48} />
                <p>No allocations found for this {type}.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  const renderLockedTab = (label: string) => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 32px', textAlign: 'center', color: '#605e5c' }}>
      <Shield size={36} style={{ color: '#c8c6c4', marginBottom: 14 }} />
      <h3 style={{ fontSize: 17, fontWeight: 700, color: '#323130', margin: '0 0 8px' }}>Feature not available</h3>
      <p style={{ fontSize: 13, maxWidth: 360, lineHeight: 1.6, margin: 0 }}>
        <strong>{label}</strong> is not included in your current plan. Upgrade to access this feature.
      </p>
    </div>
  )

  return (
    <div>
      <div className={s.pageHeader}>
        <div>
          <h2 className={s.pageTitle}>Reports</h2>
          <p className={s.pageSubtitle}>Profitability, utilization, forecast, and resource allocation analysis</p>
        </div>
      </div>

      <div className={rs.reportTabs}>
        {reportTabs.map(t => {
          const locked = t.requires ? !canAccess(t.requires) : false
          return (
            <button
              key={t.id}
              className={`${rs.reportTab} ${selectedReport === t.id ? rs.active : ''} ${locked ? rs.lockedTab : ''}`}
              onClick={() => setSelectedReport(t.id)}
              title={locked ? 'Upgrade your plan to access this feature' : undefined}
              data-tour-report={t.id}
            >
              {t.label}
              {locked && <Shield size={11} style={{ marginLeft: 5, opacity: 0.55 }} />}
            </button>
          )
        })}
      </div>

      {selectedReport === 'profitability' && (
        <div>
          <div className={s.card}>
            <div className={s.filterBar}>
              <input className={s.filterInput} placeholder="Filter project..." value={profFilter.name} onChange={e => setProfFilter(f => ({ ...f, name: e.target.value }))} />
              <input className={s.filterInput} placeholder="Filter customer..." value={profFilter.customer} onChange={e => setProfFilter(f => ({ ...f, customer: e.target.value }))} />
              <select className={s.filterSelect} value={profFilter.status} onChange={e => setProfFilter(f => ({ ...f, status: e.target.value }))}>
                <option value="">All Statuses</option>
                <option>Active</option><option>Tentative</option><option>On Hold</option><option>Closed</option>
              </select>
              <button className={s.secondaryBtn} style={{ marginLeft: 'auto' }} onClick={() => exportCSV(filteredProfitability, 'profitability.csv')}>
                <Download size={13} /> Export
              </button>
            </div>
            <table className={s.table}>
              <thead>
                <tr>
                  <th>Project</th><th>Customer</th><th>Status</th>
                  <th style={{ textAlign: 'right' }}>Revenue (USD)</th>
                  <th style={{ textAlign: 'right' }}>Cost (USD)</th>
                  <th style={{ textAlign: 'right' }}>Profit (USD)</th>
                  <th style={{ textAlign: 'right' }}>Margin</th>
                </tr>
              </thead>
              <tbody>
                {filteredProfitability.length === 0 && <tr><td colSpan={7} className={s.emptyState}>No data</td></tr>}
                {filteredProfitability.map(r => (
                  <tr key={r.projectId}>
                    <td style={{ fontWeight: 600 }}>{r.projectTitle}</td>
                    <td>{r.customerName}</td>
                    <td><span className={rs.statusBadge} style={{ background: r.status === 'Active' ? '#e6f4ea' : r.status === 'Tentative' ? '#deecf9' : '#f3f2f1', color: r.status === 'Active' ? '#107c10' : r.status === 'Tentative' ? '#0078d4' : '#605e5c' }}>{r.status}</span></td>
                    <td style={{ textAlign: 'right' }}>{currencyService.formatCurrency(r.totalRevenue, 'USD', 0)}</td>
                    <td style={{ textAlign: 'right' }}>{currencyService.formatCurrency(r.totalCost, 'USD', 0)}</td>
                    <td style={{ textAlign: 'right', color: r.totalProfit >= 0 ? '#107c10' : '#d13438', fontWeight: 600 }}>
                      {currencyService.formatCurrency(r.totalProfit, 'USD', 0)}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div className={rs.marginBar}>
                        <div className={rs.marginFill} style={{ width: `${Math.max(0, Math.min(100, r.profitMargin))}%`, background: r.profitMargin >= 20 ? '#107c10' : r.profitMargin >= 0 ? '#ca5010' : '#d13438' }} />
                        <span className={rs.marginPct}>{r.profitMargin.toFixed(1)}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              {filteredProfitability.length > 0 && (
                <tfoot>
                  <tr style={{ background: '#f3f2f1', fontWeight: 600 }}>
                    <td colSpan={3}>Totals</td>
                    <td style={{ textAlign: 'right' }}>{currencyService.formatCurrency(filteredProfitability.reduce((s, r) => s + r.totalRevenue, 0), 'USD', 0)}</td>
                    <td style={{ textAlign: 'right' }}>{currencyService.formatCurrency(filteredProfitability.reduce((s, r) => s + r.totalCost, 0), 'USD', 0)}</td>
                    <td style={{ textAlign: 'right' }}>{currencyService.formatCurrency(filteredProfitability.reduce((s, r) => s + r.totalProfit, 0), 'USD', 0)}</td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}

      {selectedReport === 'nonbillable' && (
        <div className={s.card}>
          <div className={s.filterBar}>
            <input className={s.filterInput} placeholder="Filter resource..." value={nbFilter.resource} onChange={e => setNbFilter(f => ({ ...f, resource: e.target.value }))} />
            <input className={s.filterInput} placeholder="Filter project..." value={nbFilter.project} onChange={e => setNbFilter(f => ({ ...f, project: e.target.value }))} />
            <button className={s.secondaryBtn} style={{ marginLeft: 'auto' }} onClick={() => exportCSV(filteredNonBillable, 'nonbillable.csv')}>
              <Download size={13} /> Export
            </button>
          </div>
          <table className={s.table}>
            <thead>
              <tr>
                <th>Resource</th><th>Project</th>
                <th style={{ textAlign: 'right' }}>Non-Billable Hours</th>
                <th style={{ textAlign: 'right' }}>Cost (USD)</th>
              </tr>
            </thead>
            <tbody>
              {filteredNonBillable.length === 0 && <tr><td colSpan={4} className={s.emptyState}>No non-billable records</td></tr>}
              {filteredNonBillable.map((r, i) => (
                <tr key={`${r.resourceId}-${r.projectId}-${i}`}>
                  <td style={{ fontWeight: 600 }}>{r.resourceName}</td>
                  <td>{r.projectTitle}</td>
                  <td style={{ textAlign: 'right' }}>{r.nonBillableHours.toFixed(1)}h</td>
                  <td style={{ textAlign: 'right' }}>{currencyService.formatCurrency(r.nonBillableCost, 'USD', 0)}</td>
                </tr>
              ))}
            </tbody>
            {filteredNonBillable.length > 0 && (
              <tfoot>
                <tr style={{ background: '#f3f2f1', fontWeight: 600 }}>
                  <td colSpan={2}>Totals</td>
                  <td style={{ textAlign: 'right' }}>{filteredNonBillable.reduce((s, r) => s + r.nonBillableHours, 0).toFixed(1)}h</td>
                  <td style={{ textAlign: 'right' }}>{currencyService.formatCurrency(filteredNonBillable.reduce((s, r) => s + r.nonBillableCost, 0), 'USD', 0)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {selectedReport === 'utilization' && (
        selectedResourceId ? (
          <TimelineView
            items={generateResourceTimeline(selectedResourceId)}
            type="resource"
            onBack={() => setSelectedResourceId(null)}
            title={resources.find(r => r.id === selectedResourceId)?.title || 'Resource'}
          />
        ) : (
          <div>
            <div className={rs.utilNav}>
              <div className={rs.utilViewBtns}>
                {(['day', 'week', 'month', 'quarter', 'year'] as ViewType[]).map(v => (
                  <button key={v} className={`${rs.utilViewBtn} ${utilView === v ? rs.active : ''}`} onClick={() => setUtilView(v)}>
                    {v.charAt(0).toUpperCase() + v.slice(1)}
                  </button>
                ))}
              </div>
              <div className={rs.utilNavBtns}>
                <button className={rs.navArrow} onClick={() => navigateUtil(-1)}><ArrowLeft size={13} /></button>
                <span className={rs.periodLabel}>{dateCalculationService.formatDateRange(utilStart, utilEnd, utilView)}</span>
                <button className={rs.navArrow} onClick={() => navigateUtil(1)}><ArrowRight size={13} /></button>
                <button className={rs.todayBtn} onClick={() => setUtilDate(new Date())}>Today</button>
              </div>
            </div>

            <div className={s.card}>
              <table className={s.table}>
                <thead>
                  <tr>
                    <th>Resource</th><th>Role</th><th>Available Days</th>
                    <th style={{ textAlign: 'right' }}>Time Off (d)</th>
                    <th style={{ textAlign: 'right' }}>Capacity (h)</th>
                    <th style={{ textAlign: 'right' }}>Allocated (h)</th>
                    <th>Utilization</th>
                  </tr>
                </thead>
                <tbody>
                  {utilizationData.length === 0 && <tr><td colSpan={7} className={s.emptyState}>No active resources</td></tr>}
                  {utilizationData.map(({ resource, capacity, allocated, utilization, workingDays, timeOffDays }) => {
                    const barColor = utilization > 100 ? '#d13438' : utilization >= 80 ? '#107c10' : utilization >= 50 ? '#0078d4' : '#ca5010'
                    return (
                      <tr key={resource.id}>
                        <td style={{ fontWeight: 600 }}>
                          <button
                            onClick={() => setSelectedResourceId(resource.id)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#0078d4',
                              textDecoration: 'underline',
                              cursor: 'pointer',
                              fontWeight: 600,
                              fontSize: 'inherit',
                              padding: 0
                            }}
                          >
                            {resource.title}
                          </button>
                        </td>
                      <td>{resource.primary_role}</td>
                      <td>{workingDays}</td>
                      <td style={{ textAlign: 'right', color: timeOffDays > 0 ? '#ca5010' : '#a19f9d' }}>
                        {timeOffDays > 0 ? timeOffDays : '—'}
                      </td>
                      <td style={{ textAlign: 'right' }}>{capacity.toFixed(1)}</td>
                      <td style={{ textAlign: 'right' }}>{allocated.toFixed(1)}</td>
                      <td style={{ minWidth: 160 }}>
                        <div className={rs.marginBar}>
                          <div className={rs.marginFill} style={{ width: `${Math.min(100, utilization)}%`, background: barColor }} />
                          <span className={rs.marginPct} style={{ color: barColor }}>{utilization.toFixed(0)}%</span>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
        )
      )}

      {selectedReport === 'revenue' && (!canAccess('has_revenue_forecast') ? renderLockedTab('Revenue Forecast') : (
        <div>
          <div className={s.card}>
            <div className={s.filterBar}>
              <input className={s.filterInput} placeholder="Filter customer..." value={revFilter.customer} onChange={e => setRevFilter(f => ({ ...f, customer: e.target.value }))} />
              <select className={s.filterSelect} value={revFilter.status} onChange={e => setRevFilter(f => ({ ...f, status: e.target.value }))}>
                <option value="">All Statuses</option>
                <option>Active</option><option>Tentative</option><option>On Hold</option><option>Closed</option>
              </select>
              <button className={s.secondaryBtn} style={{ marginLeft: 'auto' }}
                onClick={() => exportCSV(filteredRevenueForecast.map(r => ({
                  project: r.proj.title, customer: r.customer?.title || '', status: r.proj.status,
                  ...Object.fromEntries(revenueForecastData.months.map((m, i) => [m.label, r.monthlyRevenue[i].toFixed(0)])),
                  total: r.total.toFixed(0)
                })), 'revenue-forecast.csv')}>
                <Download size={13} /> Export
              </button>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table className={s.table}>
                <thead>
                  <tr>
                    <th>Project</th><th>Customer</th><th>Status</th>
                    {revenueForecastData.months.map(m => (
                      <th key={m.label} style={{ textAlign: 'right' }}>{m.label}</th>
                    ))}
                    <th style={{ textAlign: 'right' }}>6-Mo Total</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRevenueForecast.length === 0 && <tr><td colSpan={3 + revenueForecastData.months.length + 1} className={s.emptyState}>No forecast data in range</td></tr>}
                  {filteredRevenueForecast.map(r => (
                    <tr key={r.proj.id}>
                      <td style={{ fontWeight: 600 }}>{r.proj.title}</td>
                      <td>{r.customer?.title || '—'}</td>
                      <td><span className={rs.statusBadge} style={{ background: r.proj.status === 'Active' ? '#e6f4ea' : r.proj.status === 'Tentative' ? '#deecf9' : '#f3f2f1', color: r.proj.status === 'Active' ? '#107c10' : r.proj.status === 'Tentative' ? '#0078d4' : '#605e5c' }}>{r.proj.status}</span></td>
                      {r.monthlyRevenue.map((rev, i) => (
                        <td key={i} style={{ textAlign: 'right', color: rev > 0 ? '#323130' : '#a19f9d' }}>{rev > 0 ? currencyService.formatCurrency(rev, 'USD', 0) : '—'}</td>
                      ))}
                      <td style={{ textAlign: 'right', fontWeight: 700 }}>{currencyService.formatCurrency(r.total, 'USD', 0)}</td>
                    </tr>
                  ))}
                </tbody>
                {filteredRevenueForecast.length > 0 && (
                  <tfoot>
                    <tr style={{ background: '#f3f2f1', fontWeight: 700 }}>
                      <td colSpan={3}>Monthly Totals</td>
                      {revenueForecastData.months.map((m, i) => {
                        const mt = filteredRevenueForecast.reduce((s, r) => s + r.monthlyRevenue[i], 0)
                        return <td key={i} style={{ textAlign: 'right' }}>{currencyService.formatCurrency(mt, 'USD', 0)}</td>
                      })}
                      <td style={{ textAlign: 'right' }}>{currencyService.formatCurrency(filteredRevenueForecast.reduce((s, r) => s + r.total, 0), 'USD', 0)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          <div className={rs.revSummaryGrid}>
            {revenueForecastData.months.map((m, i) => {
              const total = filteredRevenueForecast.reduce((s, r) => s + r.monthlyRevenue[i], 0)
              const max = Math.max(...revenueForecastData.months.map((_, j) => filteredRevenueForecast.reduce((s, r) => s + r.monthlyRevenue[j], 0)))
              const pct = max > 0 ? (total / max) * 100 : 0
              return (
                <div key={m.label} className={rs.revMonthCard}>
                  <div className={rs.revMonthLabel}>{m.label}</div>
                  <div className={rs.revMonthAmount}>{currencyService.formatCurrency(total, 'USD', 0)}</div>
                  <div className={rs.revMonthBar}>
                    <div className={rs.revMonthBarFill} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ))}

      {selectedReport === 'forecastvactual' && (!canAccess('has_revenue_forecast') ? renderLockedTab('Forecast vs Actual') : (
        <div className={s.card}>
          <div className={s.filterBar}>
            <input className={s.filterInput} placeholder="Filter resource..." value={fvaFilter.resource} onChange={e => setFvaFilter(f => ({ ...f, resource: e.target.value }))} />
            <button className={s.secondaryBtn} style={{ marginLeft: 'auto' }}
              onClick={() => exportCSV(filteredForecastVsActual.map(r => ({
                resource: r.resource.title, forecast: r.forecastHours.toFixed(1),
                actualBillable: r.actualBillable.toFixed(1), actualNonBillable: r.actualNonBillable.toFixed(1),
                actualTotal: r.actualTotal.toFixed(1), variance: r.variance.toFixed(1), variancePct: r.variancePct.toFixed(1)
              })), 'forecast-vs-actual.csv')}>
              <Download size={13} /> Export
            </button>
          </div>
          <table className={s.table}>
            <thead>
              <tr>
                <th>Resource</th><th>Role</th>
                <th style={{ textAlign: 'right' }}>Forecast (h)</th>
                <th style={{ textAlign: 'right' }}>Actual Billable (h)</th>
                <th style={{ textAlign: 'right' }}>Actual Non-Billable (h)</th>
                <th style={{ textAlign: 'right' }}>Actual Total (h)</th>
                <th>Variance</th>
              </tr>
            </thead>
            <tbody>
              {filteredForecastVsActual.length === 0 && <tr><td colSpan={7} className={s.emptyState}>No forecast or timesheet data available</td></tr>}
              {filteredForecastVsActual.map(r => {
                const varColor = r.variance > 0 ? '#107c10' : r.variance < 0 ? '#d13438' : '#605e5c'
                const barPct = r.forecastHours > 0 ? Math.min(100, (r.actualTotal / r.forecastHours) * 100) : 0
                const barColor = barPct > 100 ? '#d13438' : barPct >= 80 ? '#107c10' : '#0078d4'
                return (
                  <tr key={r.resource.id}>
                    <td style={{ fontWeight: 600 }}>{r.resource.title}</td>
                    <td>{r.resource.primary_role}</td>
                    <td style={{ textAlign: 'right' }}>{r.forecastHours.toFixed(1)}</td>
                    <td style={{ textAlign: 'right' }}>{r.actualBillable.toFixed(1)}</td>
                    <td style={{ textAlign: 'right' }}>{r.actualNonBillable.toFixed(1)}</td>
                    <td style={{ textAlign: 'right' }}>{r.actualTotal.toFixed(1)}</td>
                    <td style={{ minWidth: 180 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div className={rs.marginBar} style={{ flex: 1 }}>
                          <div className={rs.marginFill} style={{ width: `${barPct}%`, background: barColor }} />
                        </div>
                        <span style={{ fontSize: 11, fontWeight: 700, color: varColor, minWidth: 52, textAlign: 'right' }}>
                          {r.variance >= 0 ? '+' : ''}{r.variance.toFixed(1)}h
                        </span>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
            {filteredForecastVsActual.length > 0 && (
              <tfoot>
                <tr style={{ background: '#f3f2f1', fontWeight: 700 }}>
                  <td colSpan={2}>Totals</td>
                  <td style={{ textAlign: 'right' }}>{filteredForecastVsActual.reduce((s, r) => s + r.forecastHours, 0).toFixed(1)}</td>
                  <td style={{ textAlign: 'right' }}>{filteredForecastVsActual.reduce((s, r) => s + r.actualBillable, 0).toFixed(1)}</td>
                  <td style={{ textAlign: 'right' }}>{filteredForecastVsActual.reduce((s, r) => s + r.actualNonBillable, 0).toFixed(1)}</td>
                  <td style={{ textAlign: 'right' }}>{filteredForecastVsActual.reduce((s, r) => s + r.actualTotal, 0).toFixed(1)}</td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      ))}

      {selectedReport === 'projectanalysis' && (
        selectedProjectId ? (
          <TimelineView
            items={generateProjectTimeline(selectedProjectId)}
            type="project"
            onBack={() => setSelectedProjectId(null)}
            title={projects.find(p => p.id === selectedProjectId)?.title || 'Project'}
          />
        ) : (
          <div className={s.card}>
            <div className={s.filterBar}>
              <input className={s.filterInput} placeholder="Filter project..." value={paFilter.project} onChange={e => setPaFilter(f => ({ ...f, project: e.target.value }))} />
              <input className={s.filterInput} placeholder="Filter customer..." value={paFilter.customer} onChange={e => setPaFilter(f => ({ ...f, customer: e.target.value }))} />
              <button className={s.secondaryBtn} style={{ marginLeft: 'auto' }}
                onClick={() => exportCSV(filteredProjectAnalysis.map(r => ({
                  project: r.proj.title, customer: r.customer?.title || '', status: r.proj.status, type: r.proj.project_type,
                  resources: r.uniqueResources, forecastHours: r.totalForecastHours.toFixed(1),
                  billableHours: r.totalBillableHours.toFixed(1), nonBillableHours: r.totalNonBillableHours.toFixed(1),
                  revenue: r.totalRevenue.toFixed(0), cost: r.totalCost.toFixed(0), profit: r.profit.toFixed(0), timesheetHours: Number(r.tsHours).toFixed(1)
                })), 'project-analysis.csv')}>
                <Download size={13} /> Export
              </button>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table className={s.table}>
                <thead>
                  <tr>
                    <th>Project</th><th>Customer</th><th>Status</th><th>Type</th>
                    <th style={{ textAlign: 'right' }}>Resources</th>
                    <th style={{ textAlign: 'right' }}>Forecast (h)</th>
                    <th style={{ textAlign: 'right' }}>Billable (h)</th>
                    <th style={{ textAlign: 'right' }}>Non-Bill. (h)</th>
                    <th style={{ textAlign: 'right' }}>Revenue (USD)</th>
                    <th style={{ textAlign: 'right' }}>Cost (USD)</th>
                    <th style={{ textAlign: 'right' }}>Profit (USD)</th>
                    <th style={{ textAlign: 'right' }}>Timesheet (h)</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProjectAnalysis.length === 0 && <tr><td colSpan={12} className={s.emptyState}>No project data available</td></tr>}
                  {filteredProjectAnalysis.map(r => (
                    <tr key={r.proj.id}>
                      <td style={{ fontWeight: 600 }}>
                        <button
                          onClick={() => setSelectedProjectId(r.proj.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#0078d4',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            fontWeight: 600,
                            fontSize: 'inherit',
                            padding: 0
                          }}
                        >
                          {r.proj.title}
                        </button>
                      </td>
                    <td>{r.customer?.title || '—'}</td>
                    <td><span className={rs.statusBadge} style={{ background: r.proj.status === 'Active' ? '#e6f4ea' : r.proj.status === 'Tentative' ? '#deecf9' : '#f3f2f1', color: r.proj.status === 'Active' ? '#107c10' : r.proj.status === 'Tentative' ? '#0078d4' : '#605e5c' }}>{r.proj.status}</span></td>
                    <td>{r.proj.project_type}</td>
                    <td style={{ textAlign: 'right' }}>{r.uniqueResources}</td>
                    <td style={{ textAlign: 'right' }}>{Number(r.totalForecastHours).toFixed(1)}</td>
                    <td style={{ textAlign: 'right', color: '#107c10' }}>{Number(r.totalBillableHours).toFixed(1)}</td>
                    <td style={{ textAlign: 'right', color: '#ca5010' }}>{Number(r.totalNonBillableHours).toFixed(1)}</td>
                    <td style={{ textAlign: 'right' }}>{currencyService.formatCurrency(r.totalRevenue, 'USD', 0)}</td>
                    <td style={{ textAlign: 'right' }}>{currencyService.formatCurrency(r.totalCost, 'USD', 0)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: r.profit >= 0 ? '#107c10' : '#d13438' }}>{currencyService.formatCurrency(r.profit, 'USD', 0)}</td>
                    <td style={{ textAlign: 'right' }}>{Number(r.tsHours).toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
              {filteredProjectAnalysis.length > 0 && (
                <tfoot>
                  <tr style={{ background: '#f3f2f1', fontWeight: 700 }}>
                    <td colSpan={4}>Totals</td>
                    <td style={{ textAlign: 'right' }}>{Array.from(new Set(filteredProjectAnalysis.flatMap(r => allAllocations.filter(a => r.proj.id === a.project_id).map(a => a.resource_id)))).length}</td>
                    <td style={{ textAlign: 'right' }}>{filteredProjectAnalysis.reduce((s, r) => s + r.totalForecastHours, 0).toFixed(1)}</td>
                    <td style={{ textAlign: 'right' }}>{filteredProjectAnalysis.reduce((s, r) => s + r.totalBillableHours, 0).toFixed(1)}</td>
                    <td style={{ textAlign: 'right' }}>{filteredProjectAnalysis.reduce((s, r) => s + r.totalNonBillableHours, 0).toFixed(1)}</td>
                    <td style={{ textAlign: 'right' }}>{currencyService.formatCurrency(filteredProjectAnalysis.reduce((s, r) => s + r.totalRevenue, 0), 'USD', 0)}</td>
                    <td style={{ textAlign: 'right' }}>{currencyService.formatCurrency(filteredProjectAnalysis.reduce((s, r) => s + r.totalCost, 0), 'USD', 0)}</td>
                    <td style={{ textAlign: 'right' }}>{currencyService.formatCurrency(filteredProjectAnalysis.reduce((s, r) => s + r.profit, 0), 'USD', 0)}</td>
                    <td style={{ textAlign: 'right' }}>{filteredProjectAnalysis.reduce((s, r) => s + Number(r.tsHours), 0).toFixed(1)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
        )
      )}

      {selectedReport === 'gantt' && (!canAccess('has_gantt') ? renderLockedTab('Gantt Chart') : (
        <GanttChart
          resources={resources}
          projects={projects}
          allocations={allAllocations}
          holidays={holidays}
          timeOffRequests={timeOffRequests}
          resourceRoleTypes={resourceRoleTypes}
          customers={customers}
          orgId={organization?.id || ''}
          onRefresh={refreshData}
        />
      ))}
    </div>
  )
}
